import React from 'react';
import { useNavigate } from 'react-router-dom';

interface ListWidgetProps {
  data: any;
  config: Record<string, any>;
  loading: boolean;
  error: string | null;
  widgetType: string;
  width: number;
  height: number;
}

const LIST_META: Record<string, { title: string; emptyIcon: string; emptyText: string; navPath: string }> = {
  list_recent_activities: { title: 'Proximas Atividades', emptyIcon: '✨', emptyText: 'Nenhuma atividade pendente', navPath: '/activities' },
  list_recent_deals: { title: 'Minhas Negociacoes', emptyIcon: '📊', emptyText: 'Nenhuma negociacao encontrada', navPath: '/deals' },
  list_recent_conversations: { title: 'Conversas Recentes', emptyIcon: '💬', emptyText: 'Nenhuma conversa pendente', navPath: '/messaging' },
};

const ACTIVITY_ICONS: Record<string, string> = {
  call: '📞', email: '✉️', meeting: '🤝', task: '✅', note: '📝', whatsapp: '💬',
};

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(value);

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Agora';
  if (mins < 60) return `${mins}min atras`;
  const hours = Math.floor(diff / 3600000);
  if (hours < 24) return `${hours}h atras`;
  const days = Math.floor(diff / 86400000);
  if (days === 1) return 'Ontem';
  if (days < 7) return `${days} dias atras`;
  return new Date(dateStr).toLocaleDateString('pt-BR');
}

function scheduledLabel(dateStr: string): { text: string; overdue: boolean } {
  const date = new Date(dateStr);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const target = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const diffDays = Math.round((target.getTime() - today.getTime()) / 86400000);
  if (diffDays < 0) return { text: 'Atrasada', overdue: true };
  if (diffDays === 0) return { text: `Hoje ${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`, overdue: false };
  if (diffDays === 1) return { text: 'Amanha', overdue: false };
  if (diffDays < 7) return { text: `${diffDays} dias`, overdue: false };
  return { text: date.toLocaleDateString('pt-BR'), overdue: false };
}

const ListWidget: React.FC<ListWidgetProps> = ({ data, loading, error, widgetType }) => {
  const navigate = useNavigate();
  const meta = LIST_META[widgetType] || { title: 'Lista', emptyIcon: '📋', emptyText: 'Sem dados', navPath: '/' };
  const items: any[] = Array.isArray(data) ? data : [];

  if (loading) {
    return (
      <ListShell title={meta.title} navPath={meta.navPath} onNav={() => navigate(meta.navPath)}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 'var(--space-8)' }}>
          <div style={{ width: '24px', height: '24px', border: '3px solid var(--border-light)', borderTopColor: 'var(--color-primary-600)', borderRadius: '50%', animation: 'spin 0.6s linear infinite' }} />
        </div>
      </ListShell>
    );
  }

  if (error) {
    return (
      <ListShell title={meta.title} navPath={meta.navPath} onNav={() => navigate(meta.navPath)}>
        <div style={{ textAlign: 'center', padding: 'var(--space-6)', color: 'var(--text-tertiary)', fontSize: 'var(--text-sm)' }}>
          {error}
        </div>
      </ListShell>
    );
  }

  return (
    <ListShell title={meta.title} navPath={meta.navPath} onNav={() => navigate(meta.navPath)}>
      <div style={{ padding: 'var(--space-3)' }}>
        {items.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 'var(--space-6)', color: 'var(--text-tertiary)' }}>
            <span style={{ fontSize: '32px', display: 'block', marginBottom: 'var(--space-2)' }}>{meta.emptyIcon}</span>
            {meta.emptyText}
          </div>
        ) : (
          items.map((item: any) => {
            if (widgetType === 'list_recent_activities') return <ActivityRow key={item.id} item={item} onClick={() => navigate(meta.navPath)} />;
            if (widgetType === 'list_recent_deals') return <DealRow key={item.id} item={item} onClick={() => navigate(meta.navPath)} />;
            if (widgetType === 'list_recent_conversations') return <ConversationRow key={item.id} item={item} onClick={() => navigate(`/messaging?conversation=${item.id}`)} />;
            return null;
          })
        )}
      </div>
    </ListShell>
  );
};

function ListShell({ title, navPath, onNav, children }: { title: string; navPath: string; onNav: () => void; children: React.ReactNode }) {
  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <div style={{
        padding: 'var(--space-4) var(--space-5)',
        borderBottom: '1px solid var(--border-light)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}>
        <h3 style={{ fontSize: 'var(--text-base)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--text-primary)', margin: 0 }}>
          {title}
        </h3>
        <button onClick={onNav} style={{ background: 'transparent', border: 'none', color: 'var(--color-primary-600)', fontSize: 'var(--text-sm)', cursor: 'pointer', fontWeight: 500 }}>
          Ver todas →
        </button>
      </div>
      <div style={{ flex: 1, overflow: 'auto', minHeight: 0 }}>{children}</div>
    </div>
  );
}

function ActivityRow({ item, onClick }: { item: any; onClick: () => void }) {
  const sched = scheduledLabel(item.scheduled_at);
  return (
    <div onClick={onClick} style={rowStyle}>
      <span style={{ fontSize: '20px' }}>{ACTIVITY_ICONS[item.activity_type] || '📌'}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={rowTitleStyle}>{item.title}</div>
        <div style={rowSubStyle}>{item.lead_name || item.deal_name || ''}</div>
      </div>
      <span style={{ fontSize: 'var(--text-xs)', color: sched.overdue ? '#ef4444' : 'var(--text-secondary)', fontWeight: 500, whiteSpace: 'nowrap' }}>
        {sched.text}
      </span>
    </div>
  );
}

function DealRow({ item, onClick }: { item: any; onClick: () => void }) {
  return (
    <div onClick={onClick} style={rowStyle}>
      <div style={{ width: '8px', height: '36px', borderRadius: '4px', background: item.stage_color || '#6366f1', flexShrink: 0 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={rowTitleStyle}>{item.lead_name}</div>
        <div style={rowSubStyle}>{item.stage_name}</div>
      </div>
      <div style={{ textAlign: 'right' }}>
        <div style={{ fontSize: 'var(--text-sm)', fontWeight: 600, color: 'var(--text-primary)' }}>{formatCurrency(item.total_value)}</div>
        <div style={rowSubStyle}>{timeAgo(item.updated_at)}</div>
      </div>
    </div>
  );
}

function ConversationRow({ item, onClick }: { item: any; onClick: () => void }) {
  return (
    <div onClick={onClick} style={rowStyle}>
      <div style={{
        width: '40px', height: '40px', borderRadius: 'var(--radius-full)',
        background: item.contact_avatar ? `url(${item.contact_avatar}) center/cover` : 'var(--bg-gradient)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: 'white', fontWeight: 600, fontSize: 'var(--text-sm)', flexShrink: 0,
      }}>
        {!item.contact_avatar && (item.contact_name?.[0]?.toUpperCase() || '?')}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
          <span style={{ ...rowTitleStyle, fontWeight: item.unread_count > 0 ? 600 : 500 }}>{item.contact_name}</span>
          {item.unread_count > 0 && (
            <span style={{ background: '#ef4444', color: 'white', fontSize: '10px', fontWeight: 600, padding: '1px 6px', borderRadius: '10px', minWidth: '18px', textAlign: 'center' }}>
              {item.unread_count}
            </span>
          )}
        </div>
        <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {typeof item.last_message === 'object' && item.last_message?.content
            ? item.last_message.content
            : (typeof item.last_message === 'string' ? item.last_message : 'Sem mensagens')}
        </div>
      </div>
      <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)', whiteSpace: 'nowrap' }}>
        {item.last_message_at ? timeAgo(item.last_message_at) : ''}
      </span>
    </div>
  );
}

const rowStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 'var(--space-3)',
  padding: 'var(--space-3)', borderRadius: 'var(--radius-lg)',
  cursor: 'pointer', transition: 'background 0.15s',
};

const rowTitleStyle: React.CSSProperties = {
  fontSize: 'var(--text-sm)', fontWeight: 500, color: 'var(--text-primary)',
  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
};

const rowSubStyle: React.CSSProperties = {
  fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)',
};

export default ListWidget;
